/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Grid, Heading, Text, Table, Thead, Tbody, Tr, Th, Td,
  Badge, Input, InputGroup, InputLeftElement, Select, Spinner, Icon, Button,
  Progress, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, useDisclosure, useColorModeValue
} from '@chakra-ui/react';
import { FaSearch, FaChartLine, FaRobot } from 'react-icons/fa';
import axios from 'axios';

const scoreColor = (score) => {
  if (score >= 80) return 'green';
  if (score >= 50) return 'yellow';
  return 'red';
};

const ScoreCell = ({ score }) => (
  <Box minW="90px">
    <Text fontSize="xs" fontWeight="800" color={`${scoreColor(score)}.500`} mb={1}>{score}%</Text>
    <Progress value={score} size="xs" colorScheme={scoreColor(score)} borderRadius="full" />
  </Box>
);

const EmployeeInsights = () => {
  const [employees, setEmployees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empSummary, setEmpSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const token = JSON.parse(localStorage.getItem('user'))?.token || '';

  const cardBg = useColorModeValue('white', 'var(--bg-surface, #161428)');
  const textDark = useColorModeValue('#0a2628', 'var(--text-main, #EDE9FF)');
  const textMuted = useColorModeValue('#7a9c9f', 'var(--text-muted, #9B98BC)');
  const cardBorder = useColorModeValue('gray.200', 'rgba(255,255,255,0.08)');

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/insights/all`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setEmployees(res.data);
        setFiltered(res.data);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    let result = employees;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.fullName?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q)
      );
    }
    if (deptFilter) result = result.filter(e => e.department === deptFilter);
    setFiltered(result);
  }, [search, deptFilter, employees]);

  const openSummary = async (emp) => {
    setSelectedEmp(emp);
    setEmpSummary('');
    setSummaryLoading(true);
    onOpen();
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/insights/summary/${emp.userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmpSummary(res.data.summary);
    } catch (err) {
      setEmpSummary('Unable to generate summary. Please try again.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
  const avg = (key) =>
    employees.length
      ? Math.round(employees.reduce((s, e) => s + (e[key] || 0), 0) / employees.length)
      : 0;

  return (
    <Box p={6}>
      <Flex align="center" gap={3} mb={8}>
        <Box p={3} bg="purple.50" borderRadius="2xl" color="purple.600">
          <FaChartLine size={24} />
        </Box>
        <Box>
          <Heading size="lg" fontWeight="900" color={textDark}>Employee Insights</Heading>
          <Text fontSize="sm" color={textMuted} fontWeight="600">
            Company-wide performance scores across quizzes, courses, and compliance.
          </Text>
        </Box>
      </Flex>

      {/* Summary cards */}
      <Grid templateColumns={{ base: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }} gap={4} mb={8}>
        {[
          { label: 'Avg Overall Score', value: avg('overallScore'), color: 'purple' },
          { label: 'Avg Quiz Score',    value: avg('quizScore'),    color: 'blue'   },
          { label: 'Avg Completion',    value: avg('completionScore'), color: 'teal' },
          { label: 'Avg Compliance',    value: avg('complianceScore'), color: 'green'},
        ].map(({ label, value, color }) => (
          <Box
            key={label}
            bg={cardBg} border="1px solid" borderColor={cardBorder}
            borderRadius="2xl" p={5} textAlign="center"
          >
            <Text fontSize="3xl" fontWeight="900" color={`${color}.500`}>{value}%</Text>
            <Text fontSize="xs" color={textMuted} fontWeight="700" mt={1}>{label}</Text>
          </Box>
        ))}
      </Grid>

      {/* Filters */}
      <Flex gap={4} mb={5} wrap="wrap">
        <InputGroup maxW="280px">
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color={textMuted} boxSize={3.5} />
          </InputLeftElement>
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            bg={cardBg} borderRadius="full" fontSize="sm"
          />
        </InputGroup>
        <Select
          maxW="200px" placeholder="All Departments"
          value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
          bg={cardBg} borderRadius="full" fontSize="sm"
        >
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </Select>
      </Flex>

      <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" overflow="hidden" boxShadow="sm">
        {isLoading ? (
          <Flex justify="center" p={10}><Spinner /></Flex>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg="blackAlpha.50">
                <Tr>
                  <Th>Employee</Th>
                  <Th>Department</Th>
                  <Th>Quiz (30%)</Th>
                  <Th>Completion (40%)</Th>
                  <Th>Compliance (30%)</Th>
                  <Th>Overall Score</Th>
                  <Th>AI Summary</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map(emp => (
                  <Tr key={emp.userId}>
                    <Td>
                      <Text fontWeight="700" color={textDark} fontSize="sm">{emp.fullName}</Text>
                      <Text fontSize="xs" color={textMuted}>{emp.email}</Text>
                    </Td>
                    <Td>
                      <Badge borderRadius="full" px={2} fontSize="xs">
                        {emp.department || 'General'}
                      </Badge>
                    </Td>
                    <Td><ScoreCell score={emp.quizScore} /></Td>
                    <Td><ScoreCell score={emp.completionScore} /></Td>
                    <Td><ScoreCell score={emp.complianceScore} /></Td>
                    <Td>
                      <Badge
                        colorScheme={scoreColor(emp.overallScore)}
                        fontSize="sm" fontWeight="900"
                        borderRadius="full" px={3} py={1}
                      >
                        {emp.overallScore}%
                      </Badge>
                    </Td>
                    <Td>
                      <Button
                        size="xs" colorScheme="purple" variant="outline"
                        leftIcon={<FaRobot />} borderRadius="full"
                        onClick={() => openSummary(emp)}
                      >
                        Summary
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {filtered.length === 0 && (
              <Text p={8} color={textMuted} textAlign="center" fontSize="sm">
                No employees found.
              </Text>
            )}
          </Box>
        )}
      </Box>

      {/* AI Summary Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader>
            <Flex align="center" gap={2}>
              <FaRobot color="#7c3aed" />
              <Text fontWeight="900" fontSize="md">AI Summary — {selectedEmp?.fullName}</Text>
            </Flex>
            <Text fontSize="xs" color={textMuted} fontWeight="600" mt={1}>
              {selectedEmp?.department || 'General'} · Overall Score: {selectedEmp?.overallScore}%
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {summaryLoading ? (
              <Flex align="center" gap={3} py={4}>
                <Spinner color="purple.500" />
                <Text fontSize="sm" color={textMuted}>Generating summary from course and quiz data...</Text>
              </Flex>
            ) : (
              <Box bg={useColorModeValue('purple.50', 'rgba(157,148,248,0.1)')} borderRadius="xl" p={5}>
                <Text fontSize="sm" lineHeight={1.9} color={useColorModeValue('purple.900', 'var(--text-main, #EDE9FF)')} fontWeight="500">
                  {empSummary}
                </Text>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EmployeeInsights;